/**
 * /student/exam/:attemptId/submitted — after the paper is in.
 *
 * Two jobs, and sometimes a third: FINISH UPLOADING the recording, get out of
 * the way, and — only for an exam that asks for it — collect the answer-sheet
 * photos from the phone. No score, no celebration: the result is the admin's
 * to release.
 *
 * The answer-sheet step is off unless the exam turns it on. It was written for
 * a written paper, where the working on the rough sheets is the evidence. A
 * multiple-choice or coding exam produces no sheets, so the step asked the
 * student to photograph nothing — and it did so at the one moment the only
 * instruction that matters is "keep this page open until the recording
 * finishes uploading". Two instructions competing, one of them pointless.
 *
 * The first job used to be missing, which made this page actively harmful. It
 * counted what was still queued in IndexedDB and said "Uploading your recording
 * — N segments left. Keep this page open until it finishes", but nothing on
 * this page uploaded anything: the uploaders belonged to the recorders on the
 * exam page, which had just unmounted. The number could not move and the wait
 * could not end. A student who did exactly as instructed sat watching a frozen
 * counter, then closed the tab and lost the footage — and the bigger the
 * backlog, the more certainly it was lost. `useUploadDrain` now owns uploaders
 * here, so the promise this page makes is one it can keep.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BarChart3, Check, TriangleAlert, Upload } from "lucide-react";
import type { AttemptStateResponse } from "@exam/shared";
import { AppShell } from "@/components/AppShell";
import { Button, Card, CardBody, CardHeader } from "@/components/ui";
import { cn } from "@/lib/cn";
import { getAttempt, setStage } from "@/lib/exam-api";
import { ServerClock, timed } from "@/lib/exam/serverClock";
import { formatMB, useUploadDrain } from "@/lib/recorder";
import { exitFullscreen } from "@/lib/proctor";

const POLL_MS = 3_000;

/** What this device is responsible for uploading. The phone drains its own. */
const LAPTOP_SOURCES = ["webcam", "screen"] as const;

export function SubmittedPage() {
  const { attemptId = "" } = useParams();
  const navigate = useNavigate();
  const clock = useMemo(() => new ServerClock(), []);

  const [state, setState] = useState<AttemptStateResponse | null>(null);
  const [finished, setFinished] = useState(false);

  // The laptop's own two recordings. The phone drains its own queue from the
  // phone page, which stays open on its answer-sheet step.
  const drain = useUploadDrain({
    attemptId,
    sources: LAPTOP_SOURCES,
    // Stops once the student has said they are done, so the loops are not left
    // running behind a page they have navigated away from.
    enabled: !finished,
  });

  // The exam is over — release fullscreen so the student is not trapped.
  useEffect(() => {
    void exitFullscreen();
  }, []);

  const wantsSheets = state?.exam.collect_answer_sheets ?? false;

  // Ask the phone for answer sheets — only when this exam wants them. Putting
  // the phone into `submitted_upload` otherwise leaves it sitting on a capture
  // screen nobody is going to use, for an exam that has no sheets to capture.
  useEffect(() => {
    if (!attemptId || finished || !wantsSheets) return;
    void setStage(attemptId, "submitted_upload").catch(() => undefined);
  }, [attemptId, finished, wantsSheets]);

  useEffect(() => {
    if (!attemptId) return;

    const poll = async () => {
      try {
        const next = await timed(clock, () =>
          getAttempt(attemptId, { includePaper: false }),
        );
        setState(next);
      } catch {
        /* transient */
      }
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(id);
  }, [attemptId, clock]);

  const sheets = state?.media_counts.answer_sheet ?? 0;
  const uploadsDone = drain.done;
  const { pending, pendingBytes: outstandingBytes, refused, lastErrorMessage } =
    drain.status;

  const finish = useCallback(async () => {
    setFinished(true);
    await setStage(attemptId, "done").catch(() => undefined);
    navigate("/student", { replace: true });
  }, [attemptId, navigate]);

  return (
    <AppShell title="Submitted">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <Card>
          <CardBody className="flex items-start gap-3">
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-sm border border-answered/30 bg-answered-soft">
              <Check className="size-3.5 text-answered" />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-ink">
                Your exam has been submitted
              </h1>
              <p className="mt-0.5 text-xs text-ink-muted">
                {state?.exam.title}
                {state?.attempt.status === "auto_submitted"
                  ? " · submitted automatically when the time ran out"
                  : ""}
              </p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={
              wantsSheets ? "Upload your answer sheets" : "Finishing up"
            }
            description={
              wantsSheets
                ? "Photograph your rough sheets on your phone now, and keep this page open until the recording has finished uploading."
                : "Keep this page open until the recording has finished uploading. You can close it once it says so."
            }
          />
          <CardBody>
            {wantsSheets ? (
            <>
            <ol className="flex flex-col gap-1.5">
              {[
                "Pick up your phone — it is showing the answer-sheet step.",
                "Photograph every sheet you wrote on, front and back.",
                "Check each photo is readable before accepting it.",
              ].map((line, i) => (
                <li key={line} className="flex gap-2 text-xs text-ink-muted">
                  <span className="tabular shrink-0 text-ink-faint">
                    {i + 1}.
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>

            <div
              className={cn(
                "mt-4 flex items-center gap-2 rounded-md border px-3 py-2",
                sheets > 0
                  ? "border-answered/30 bg-answered-soft"
                  : "border-border bg-surface-sunken",
              )}
            >
              {sheets > 0 ? (
                <Check className="size-3.5 text-answered" />
              ) : (
                <Upload className="size-3.5 text-ink-faint" />
              )}
              <span className="text-xs text-ink">
                <span className="tabular font-medium">{sheets}</span>
                <span className="text-ink-muted">
                  {" "}
                  answer sheet {sheets === 1 ? "photo" : "photos"} uploaded
                </span>
              </span>
            </div>
            </>
            ) : null}

            {/* Ordered by what is actually true, not by severity: `blocked`
                has to be tested BEFORE the uploading branch, because
                "not done" is true in both states and testing it first
                rendered "Uploading your recording — 0 video segments left"
                over a backlog that had been refused and would never move. */}
            {pending > 0 ? (
              // Real progress from a real uploader running on this page.
              <div className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2">
                <div className="flex items-start gap-2">
                  <Upload className="mt-0.5 size-3.5 shrink-0 text-ink-faint" />
                  <div className="min-w-0 flex-1">
                    <p className="text-2xs text-ink-muted">
                      Uploading your recording —{" "}
                      <span className="tabular font-medium text-ink">
                        {pending}
                      </span>{" "}
                      video {pending === 1 ? "segment" : "segments"}
                      {outstandingBytes > 0 ? (
                        <>
                          {" "}
                          (
                          <span className="tabular">
                            {formatMB(outstandingBytes)}
                          </span>
                          )
                        </>
                      ) : null}{" "}
                      left. Keep this page open until it finishes.
                    </p>

                    {/* Measured from the high-water mark, so it cannot run
                        backwards when a late chunk is queued. */}
                    <div
                      className="mt-2 h-1.5 overflow-hidden rounded-full bg-border"
                      role="progressbar"
                      aria-label="Recording upload"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(drain.progress * 100)}
                    >
                      <div
                        className="h-full rounded-full bg-answered transition-[width] duration-500"
                        style={{ width: `${Math.round(drain.progress * 100)}%` }}
                      />
                    </div>

                    {lastErrorMessage ? (
                      // Named rather than hidden: on a poor connection this is
                      // the difference between "it is slow" and "it is stuck",
                      // and only the student can act on the second.
                      <p className="mt-1.5 text-2xs text-warn">
                        Last attempt failed: {lastErrorMessage}. Still retrying —
                        check your internet connection.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : drain.blocked ? (
              // Refused chunks are excluded from `pending` so the drain does
              // not loop on them, which makes "nothing pending" and "it all
              // arrived" two different facts. Saying so plainly beats a green
              // tick over footage that never landed.
              <div className="mt-3 flex items-start gap-2 rounded-md border border-warn/40 bg-warn-soft px-3 py-2">
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-warn" />
                <p className="text-2xs text-warn">
                  <span className="tabular font-medium">{refused}</span> video{" "}
                  {refused === 1 ? "segment" : "segments"} could not be uploaded
                  — the upload window for this exam has closed. Your answers and
                  marks are unaffected. Tell whoever set the exam.
                </p>
              </div>
            ) : (
              <p className="mt-3 flex items-center gap-1.5 text-2xs text-ink-muted">
                <Check className="size-3.5 text-answered" />
                All recording uploaded from this laptop.
              </p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex flex-wrap items-center gap-3">
            <p className="min-w-0 flex-1 text-xs text-ink-muted">
              Your paper has been graded — see your score, where the marks went
              and which topics to work on next.
            </p>
            <Link to={`/student/exam/${attemptId}/result`}>
              <Button variant="primary" icon={<BarChart3 />}>
                See your result
              </Button>
            </Link>
            <Button
              variant="secondary"
              // `blocked` counts as finished: there is nothing left to wait
              // for, and leaving the student trapped on a button that can
              // never enable is how they end up force-closing the tab.
              disabled={!uploadsDone && !drain.blocked}
              onClick={() => void finish()}
            >
              {uploadsDone || drain.blocked ? "Done" : "Finishing uploads…"}
            </Button>
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
